<template>
  <div>
    <loading-panel :loading="loading"></loading-panel>

    <div class="row">
      <div class="col">
        <card>
          <div class="tabs-header d-flex align-items-end mb-3">
            <tab-rail
              :tabs="[{ name: 'Custom fields', shortName: 'Fields' }, { name: 'Tags' }]"
              v-model="activeTab"
              class="flex-grow-1"
            ></tab-rail>
            <base-button v-if="activeTab === 0" type="primary" size="sm" class="ml-3 tabs-header__action" @click="addCustomField()">
              <i class="tim-icons el-icon-plus mr-1"></i> Create field
            </base-button>
            <base-button v-if="activeTab === 1" type="primary" size="sm" class="ml-3 tabs-header__action" @click="$refs.tagTab && $refs.tagTab.openAddForm()">
              <i class="tim-icons el-icon-plus mr-1"></i> Create tag
            </base-button>
          </div>

          <div v-show="activeTab === 0">

          <filter-bar
            :filters="filterSchema"
            :initial-value="filterValues"
            :result-count="totalRows"
            :per-page="perPage"
            :per-page-options="perPageOptions"
            saved-filters-key="custom-fields"
            @change="onFiltersChanged"
            @update:perPage="onPerPageChange"
          ></filter-bar>

          <!-- Empty state: no records at all -->
          <div v-if="!loading && totalRows === 0 && !hasActiveFilters" class="cf-empty-state mt-4">
            <img src="/img/pandas/empty-custom-fields.png" alt="No custom fields" class="empty-state-img" />
            <h4 class="empty-state-text">No custom fields yet</h4>
            <p class="cf-empty-state__hint">
              Custom fields let you collect additional information from customers during booking, on waivers, and at checkout
            </p>
          </div>

          <!-- Field cards -->
          <div v-if="customFields.length > 0" class="cf-list">
            <div
              v-for="item in customFields"
              :key="'CF' + item.id"
              class="cf-card"
              :class="{ 'cf-card--archived': item.archived }"
              @click="editCustomFieldModal(item)"
            >
              <div class="cf-card__body">
                <!-- Left: type icon -->
                <div class="cf-card__type-icon">
                  <i v-if="item.type.id === 1" class="far fa-check-square"></i>
                  <i v-else-if="item.type.id === 2" class="tim-icons icon-pencil"></i>
                  <i v-else-if="item.type.id === 3" class="tim-icons icon-bullet-list-67"></i>
                </div>

                <!-- Center: label + metadata -->
                <div class="cf-card__info">
                  <div class="cf-card__label text-white">{{ item.label }}</div>

                  <!-- Row 1: field properties (what this field IS) -->
                  <div class="cf-card__meta">
                    <span class="cf-card__type-name">{{ item.type.type }}</span>

                    <!-- Inline links/policies indicator for checkbox type -->
                    <template v-if="item.type.id === 1">
                      <span v-if="item.external_links && item.external_links.length"
                            class="cf-card__meta-detail">
                        <i class="tim-icons icon-link-72 mr-1"></i>{{ item.external_links.length }} link{{ item.external_links.length > 1 ? 's' : '' }}
                      </span>
                      <span v-if="item.policies && item.policies.length"
                            class="cf-card__meta-detail cf-card__meta-detail--clickable"
                            @click.stop="openPolicy(item.policies[0])">
                        <i class="tim-icons icon-paper mr-1"></i>{{ item.policies.length }} policy{{ item.policies.length > 1 ? 'ies' : '' }}
                      </span>
                    </template>

                    <!-- Dropdown options count -->
                    <span v-if="item.type.id === 3 && item.dropdown_options && item.dropdown_options.length"
                          class="cf-card__meta-detail">
                      {{ item.dropdown_options.length }} option{{ item.dropdown_options.length > 1 ? 's' : '' }}
                    </span>
                  </div>

                  <!-- Row 2: usage (where this field is USED) -->
                  <div class="cf-card__usage">
                    <template v-if="hasUsage(item)">
                      <span class="cf-card__usage-label">Used in:</span>
                      <span class="cf-card__usage-text">{{ usageSummary(item) }}</span>
                    </template>
                    <span v-else class="cf-card__usage cf-card__usage--unused">Not used</span>
                  </div>
                </div>

                <!-- Right: badges + actions -->
                <div class="cf-card__actions">
                  <span v-if="item.archived" class="badge-tag badge-tag--archived">Archived</span>

                  <el-dropdown @click.native.stop>
                    <base-button simple type="primary" link>
                      <i style="font-size: medium" class="tim-icons el-icon-more"></i>
                    </base-button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <a class="dropdown-item mb-2" @click.prevent="editCustomFieldModal(item)" href="#">
                          <i class="tim-icons icon-pencil mr-2"></i> Edit field
                        </a>
                        <hr>
                        <a v-if="!item.archived" class="dropdown-item mb-2" @click.prevent="archiveFieldPrompt(item)" href="#">
                          <i class="tim-icons icon-trash-simple mr-2"></i> Archive field
                        </a>
                        <a v-else class="dropdown-item mb-2" @click.prevent="restoreField(item)" href="#">
                          <i class="tim-icons icon-refresh-02 mr-2"></i> Restore field
                        </a>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </div>
              </div>
            </div>
          </div>

          <!-- No results from filters -->
          <div v-if="!loading && totalRows === 0 && hasActiveFilters" class="cf-empty-state mt-4">
            <img src="/img/pandas/empty-custom-fields.png" alt="No results" class="empty-state-img" />
            <p class="cf-empty-state__hint">No custom fields match your filters</p>
          </div>

          <b-pagination
            v-on:input="getCustomFields()"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            class="mt-3 mb-1"
          ></b-pagination>
          </div>

          <tag-library-tab v-if="activeTab === 1" ref="tagTab"></tag-library-tab>
        </card>
      </div>
    </div>

    <!-- Policy preview modal -->
    <modal :show.sync="modals.policy"
           body-classes="p-0"
           modal-classes="modal-dialog-centered">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-4 py-lg-4"
            class="border-0 mb-0">
        <p class="text-white" v-html="selectedPolicy"></p>
        <div class="row mt-3 justify-content-center">
          <div class="col text-center">
            <base-button @click="modals.policy = false" type="primary">Close</base-button>
          </div>
        </div>
      </card>
    </modal>

    <!-- Custom field drawer -->
    <base-drawer
      :show="modals.custom_field"
      :title="selectedField.id ? 'Edit custom field' : 'Create custom field'"
      @close="modals.custom_field = false"
    >
      <template #header-extra>
        <div v-if="selectedField.id && selectedFieldUsageTotal > 1" class="cf-shared-warning mt-3">
          <i class="tim-icons icon-bulb-63 mr-2"></i>
          This field is used in {{ selectedFieldUsageTotal }} place{{ selectedFieldUsageTotal > 1 ? 's' : '' }}. Changes will apply everywhere.
        </div>
      </template>

      <!-- Field basics -->
      <drawer-section title="Field basics" icon="icon-app">
        <base-input label="Field type">
          <el-select
            v-model="selectedField.type"
            class="select-primary"
            value-key="id"
            placeholder="Type">
            <el-option
              v-for="(c, i) in customFieldTypes"
              class="select-primary"
              :label="c.type"
              :value="{type: c.type, id: c.id}"
              :key="'CT' + i"
            ></el-option>
          </el-select>
        </base-input>
        <base-input label="Label" v-model="selectedField.label" maxlength="1000" placeholder="e.g. How did you hear about us?"></base-input>
        <div class="cf-char-count">{{ (selectedField.label || '').length }}/1000</div>
      </drawer-section>

      <!-- Checkbox configuration -->
      <drawer-section v-if="selectedField.type.id === 1" title="Checkbox configuration" icon="icon-check-2">
        <span class="info-hint"><i class="tim-icons icon-bulb-63 mr-2"></i>Add links or policy popups that appear alongside the checkbox label</span>

        <div v-for="(e, i) in selectedField.external_links" :key="'EL' + i" class="mt-2">
          <div class="d-flex align-items-end" style="gap: 6px;">
            <div class="flex-grow-1">
              <base-input :label="'Link text ' + (i+1)" v-model="e.link_text" placeholder="e.g. View terms"></base-input>
            </div>
            <div style="margin-bottom: 12px;">
              <base-button type="danger" icon link size="sm"
                           @click="removeOption(selectedField.external_links, i)">
                <i class="tim-icons icon-simple-remove"></i>
              </base-button>
            </div>
          </div>
          <base-input label="Link URL" v-model="e.link" placeholder="https://"></base-input>
        </div>

        <div v-for="(p, i) in selectedField.policies" :key="'PO' + i" class="mt-2">
          <div class="d-flex align-items-end" style="gap: 6px;">
            <div class="flex-grow-1">
              <base-input :label="'Policy link text ' + (i+1)" v-model="p.policy_link_text" placeholder="e.g. View cancellation policy"></base-input>
            </div>
            <div style="margin-bottom: 12px;">
              <base-button type="danger" icon link size="sm"
                           @click="removeOption(selectedField.policies, i)">
                <i class="tim-icons icon-simple-remove"></i>
              </base-button>
            </div>
          </div>
          <label>Policy content</label>
          <rich-text-editor
            v-model="p.policy"
            :height="200"
            toolbar="basic"
            :faithful-load="true"
            placeholder="Enter policy content..."
          />
        </div>

        <div class="d-flex flex-wrap mt-3" style="gap: 8px;">
          <base-button type="primary" @click="addExternalLink(selectedField)" size="sm">
            <i class="tim-icons el-icon-plus"></i> Add an external link
          </base-button>
          <base-button type="primary" @click="addFieldPolicy(selectedField)" size="sm">
            <i class="tim-icons el-icon-plus"></i> Add a policy pop-up
          </base-button>
        </div>
      </drawer-section>

      <!-- Dropdown configuration -->
      <drawer-section v-if="selectedField.type.id === 3" title="Dropdown options" icon="icon-bullet-list-67">
        <span class="info-hint"><i class="tim-icons icon-bulb-63 mr-2"></i>Define the choices available in the dropdown menu</span>

        <div v-for="(d, i) in selectedField.dropdown_options" :key="'O' + i"
             class="dropdown-option-row" :class="{ 'dropdown-option-row--bordered': i > 0 }">
          <div class="d-flex align-items-end" style="gap: 6px;">
            <div class="flex-grow-1">
              <base-input :label="'Option ' + (i + 1)" v-model="d.name" placeholder="Option name"></base-input>
            </div>
            <div style="margin-bottom: 12px;">
              <el-tooltip content="Remove option" effect="light" :open-delay="300" placement="top">
                <base-button type="danger" icon link size="sm"
                             @click="removeOption(selectedField.dropdown_options, i)">
                  <i class="tim-icons icon-simple-remove"></i>
                </base-button>
              </el-tooltip>
            </div>
          </div>

          <div class="d-flex flex-wrap mt-1" style="gap: 16px;">
            <div class="d-flex align-items-center">
              <el-switch active-color="#e14eca"
                         :value="!!d.has_follow_up_field"
                         @change="val => d.has_follow_up_field = val ? 1 : 0" />
              <label class="mb-0">&nbsp; Follow up field</label>
            </div>
            <div class="d-flex align-items-center">
              <el-switch active-color="#e14eca"
                         :value="!!d.has_popup"
                         @change="val => { d.has_popup = val ? 1 : 0; if (!val) d.description = '' }" />
              <label class="mb-0">&nbsp; Popup content</label>
            </div>
          </div>

          <div v-if="d.has_follow_up_field" class="mt-2">
            <base-input label="Follow up field label" v-model="d.follow_up_field_label" placeholder="e.g. Please specify"></base-input>
          </div>

          <div v-if="d.has_popup" class="mt-2">
            <label class="popup-label">Popup content</label>
            <span class="info-hint mt-0"><i class="tim-icons icon-bulb-63 mr-2"></i>Shown to the customer when they select this option</span>
            <rich-text-editor
              v-model="d.description"
              :height="150"
              toolbar="basic"
              placeholder="Add a description, images, or other content to display in a popup..."
            />
          </div>
        </div>

        <div class="mt-3">
          <base-button type="primary" @click="addDropDownOption(selectedField)" size="sm">
            <i class="tim-icons el-icon-plus"></i> Add a dropdown option
          </base-button>
        </div>
      </drawer-section>

      <!-- Display rules -->
      <drawer-section title="Display rules" icon="icon-zoom-split">
        <span class="info-hint"><i class="tim-icons icon-bulb-63 mr-2"></i>Scope this field to events or gift cards.</span>
        <label>Events</label>
        <div class="mb-3">
          <segmented-toggle v-model="selectedField.event_scope" :options="eventScopeOptions" size="sm" />
        </div>
        <base-input v-if="selectedField.event_scope === 'specific'" label="Specific events">
          <el-select multiple
                     v-model="selectedField.visibility_conditions"
                     class="select-primary"
                     value-key="id"
                     placeholder="Select events">
            <el-option
              v-for="game in games"
              class="select-primary"
              :label="game.internal_name || game.name"
              :value="{name: game.internal_name || game.name, id: game.id}"
              :key="'G' + game.id"
            ></el-option>
          </el-select>
        </base-input>
        <div v-if="gift_card_types.length > 0">
          <label>Gift cards</label>
          <div class="mb-3">
            <segmented-toggle v-model="selectedField.gift_card_scope" :options="giftCardScopeOptions" size="sm" />
          </div>
          <base-input v-if="selectedField.gift_card_scope === 'specific'" label="Specific types">
            <el-select multiple
                       v-model="selectedField.gift_card_type_visibility_conditions"
                       class="select-primary"
                       value-key="id"
                       placeholder="Select gift card types">
              <el-option
                v-for="t in gift_card_types"
                class="select-primary"
                :label="t.internal_label || t.name"
                :value="{name: t.internal_label || t.name, id: t.id}"
                :key="'GCT' + t.id"
              ></el-option>
            </el-select>
          </base-input>
        </div>
        <span v-else class="info-hint"><i class="tim-icons icon-bulb-63 mr-2"></i>To scope this field by gift card, set up gift card types in <router-link to="/purchases/gift-cards">Purchases &gt; Gift cards</router-link>.</span>
      </drawer-section>

      <template #footer>
        <div class="d-flex justify-content-end" style="gap: 10px;">
          <base-button size="sm" @click="modals.custom_field = false" type="default">Cancel</base-button>
          <base-button size="sm" @click="saveField(selectedField)" type="primary">Save changes</base-button>
        </div>
      </template>
    </base-drawer>
  </div>
</template>

<script>
import {API_LOCATION} from "@/api-config";
import axios from "axios";
import swal from "sweetalert2";
import {Auth} from "aws-amplify";
import {Select, Option, Tooltip, Dropdown, DropdownItem, DropdownMenu, Switch} from 'element-ui';
import {BPagination} from 'bootstrap-vue';
import _ from 'lodash';
import RichTextEditor from '@/components/RichTextEditor';
import TabRail from '@/components/TabRail';
import TagLibraryTab from './TagLibraryTab.vue';

export default {
  name: 'CustomFieldsLibrary',
  components: {
    RichTextEditor,
    BPagination,
    TabRail,
    TagLibraryTab,
    [Select.name]: Select,
    [Option.name]: Option,
    [Tooltip.name]: Tooltip,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Switch.name]: Switch,
  },
  data() {
    return {
      loading: false,
      // Tabs: 0 = custom fields, 1 = tags. Seeded from ?tab=tags so the
      // booking drawer's Manage tags link deep-links here.
      activeTab: this.$route.query.tab === 'tags' ? 1 : 0,
      group: JSON.parse(localStorage.getItem('group')),
      customFields: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 25, 50],
      // Flat filter values for the shared <filter-bar>. Seeded from the URL in
      // mounted; saved-views precedence is resolved by FilterBar.
      filterValues: {
        type: '',
        label: '',
        archived: false,
      },
      // FilterBar drives the first fetch via its initial @change; this guards it.
      didInitialFetch: false,
      games: [],
      gift_card_types: [],
      eventScopeOptions: [
        {value: 'none', label: 'None'},
        {value: 'all', label: 'All events'},
        {value: 'specific', label: 'Specific events'},
      ],
      giftCardScopeOptions: [
        {value: 'none', label: 'None'},
        {value: 'any', label: 'Any gift card'},
        {value: 'specific', label: 'Specific types'},
      ],
      selectedPolicy: '',
      customFieldTypes: [
        {id: 1, type: 'Checkbox'},
        {id: 2, type: 'Input field'},
        {id: 3, type: 'Dropdown'}
      ],
      modals: {
        policy: false,
        custom_field: false,
      },
      selectedField: this.getEmptyField(),
    }
  },
  computed: {
    // Schema for the shared <filter-bar>: a label search, a type select, an archived toggle.
    filterSchema() {
      return [
        { key: 'label', type: 'text', label: 'Label', placeholder: 'Search by label', operatorLabel: 'contains', primary: true },
        { key: 'type', type: 'select', label: 'Type', primary: true,
          options: this.customFieldTypes.map(t => ({ label: t.type, value: t.id })) },
        { key: 'archived', type: 'toggle', label: 'Show archived', primary: true },
      ]
    },
    hasActiveFilters() {
      return !!this.filterValues.type || !!this.filterValues.label || !!this.filterValues.archived
    },
    selectedFieldUsageTotal() {
      if (!this.selectedField) return 0
      return (this.selectedField.template_count || 0)
        + (this.selectedField.waiver_count || 0)
        + (this.selectedField.internal_booking_count || 0)
    }
  },
  methods: {
    hasPermissions(scope) {
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      return permissions.some(element => element === scope)
    },
    hasUsage(item) {
      return (item.template_count || 0) + (item.waiver_count || 0) + (item.internal_booking_count || 0) > 0
    },
    usageSummary(item) {
      const parts = []
      if (item.template_count > 0) {
        parts.push(`${item.template_count} booking site template${item.template_count > 1 ? 's' : ''}`)
      }
      if (item.waiver_count > 0) {
        parts.push(`${item.waiver_count} waiver${item.waiver_count > 1 ? 's' : ''}`)
      }
      if (item.internal_booking_count > 0) {
        parts.push(`${item.internal_booking_count} internal booking${item.internal_booking_count > 1 ? 's' : ''}`)
      }
      return parts.join(', ')
    },
    getEmptyField() {
      return {
        id: null,
        type: {id: 1, type: 'Checkbox'},
        label: '',
        dropdown_options: [],
        external_links: [],
        policies: [],
        required: 0,
        add_alert: 0,
        interaction_required: 0,
        visibility_conditions: [],
        gift_card_type_visibility_conditions: [],
        event_scope: 'all',
        gift_card_scope: 'none',
        template_count: 0,
        waiver_count: 0,
        internal_booking_count: 0,
      }
    },
    getAuthToken() {
      return new Promise(function (resolve) {
        Auth.currentSession()
          .then(data => {
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    openPolicy(item) {
      this.selectedPolicy = item.policy
      this.modals.policy = true
    },
    removeOption(array, index) {
      array.splice(index, 1)
    },
    addQueryParameters() {
      const newQuery = {
        count: this.perPage,
        page: this.currentPage,
      }
      if (this.filterValues.type) newQuery.type = this.filterValues.type
      if (this.filterValues.label) newQuery.label = this.filterValues.label
      if (this.filterValues.archived) newQuery.archived = 'true'

      // Preserve onboarding context so the side panel stays open when the wizard
      // navigates here. Same pattern as PaymentList.vue / TimeTrackerLogs.vue.
      if (this.$route.query.onboarding) newQuery.onboarding = this.$route.query.onboarding
      if (this.$route.query.categoryId) newQuery.categoryId = this.$route.query.categoryId
      if (this.$route.query.taskId) newQuery.taskId = this.$route.query.taskId

      this.$router.replace({ query: newQuery }).catch(err => {
        if (err.name !== 'NavigationDuplicated') throw err
      })
    },
    // FilterBar change: apply new values and (re)fetch. The first change is
    // FilterBar's initial saved-views resolution and drives the first load,
    // preserving the URL page; subsequent changes reset to page 1.
    onFiltersChanged(values) {
      this.filterValues = {
        type: values.type || '',
        label: values.label || '',
        archived: !!values.archived,
      }
      if (!this.didInitialFetch) {
        this.didInitialFetch = true
        this.getCustomFields()
      } else {
        this.currentPage = 1
        this.getCustomFields()
      }
    },
    onPerPageChange(n) {
      this.perPage = n
      this.currentPage = 1
      this.getCustomFields()
    },
    addCustomField() {
      this.selectedField = this.getEmptyField()
      this.modals.custom_field = true
    },
    editCustomFieldModal(item) {
      const visibilityConditions = JSON.parse(JSON.stringify(item.visibility_conditions || []))
      const giftCardConditions = JSON.parse(JSON.stringify(item.gift_card_type_visibility_conditions || []))
      // Events encoding:
      //   visibility=0           → 'all'      (legacy "Always show", new default)
      //   visibility=1 + empty   → 'none'     (event axis explicitly skipped)
      //   visibility=1 + rows    → 'specific'
      let eventScope
      if (visibilityConditions.length > 0) eventScope = 'specific'
      else if (item.visibility) eventScope = 'none'
      else eventScope = 'all'
      // Gift cards: derived ONLY from `show_when_gift_card_in_cart` + gift card link rows.
      // Independent of `visibility`.
      let giftCardScope = 'none'
      if (giftCardConditions.length > 0) giftCardScope = 'specific'
      else if (item.show_when_gift_card_in_cart) giftCardScope = 'any'
      this.selectedField = {
        id: item.id,
        type: item.type,
        label: item.label,
        dropdown_options: JSON.parse(JSON.stringify(item.dropdown_options || [])),
        external_links: JSON.parse(JSON.stringify(item.external_links || [])),
        policies: JSON.parse(JSON.stringify(item.policies || [])),
        required: item.required,
        add_alert: item.add_alert,
        interaction_required: item.interaction_required,
        visibility_conditions: visibilityConditions,
        gift_card_type_visibility_conditions: giftCardConditions,
        event_scope: eventScope,
        gift_card_scope: giftCardScope,
        template_count: item.template_count || 0,
        waiver_count: item.waiver_count || 0,
        internal_booking_count: item.internal_booking_count || 0,
      }
      this.modals.custom_field = true
    },
    addExternalLink(item) {
      item.external_links.push({link: '', link_text: ''})
    },
    addFieldPolicy(item) {
      item.policies.push({policy_link_text: '', policy: ''})
    },
    addDropDownOption(item) {
      item.dropdown_options.push({name: '', follow_up_field_label: '', has_follow_up_field: 0, description: '', has_popup: 0})
    },
    async getGames() {
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.group.id + '/games'
      try {
        let res = await axios.get(url, config)
        this.games = res.data || []
      } catch (err) {
        console.error(err)
      }
    },
    async getGiftCardTypes() {
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.group.id + '/gift-card-types'
      try {
        let res = await axios.get(url, config)
        this.gift_card_types = (res.data && res.data.gift_card_types) || []
      } catch (err) {
        console.error(err)
      }
    },
    getOffset() {
      return (this.currentPage * this.perPage) - this.perPage
    },
    async getCustomFields() {
      this.addQueryParameters()
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.group.id + '/custom-fields'
        + '?count=' + this.perPage + '&offset=' + this.getOffset()
      if (!this.filterValues.archived) url += '&archived=0'
      if (this.filterValues.type) url += '&type=' + this.filterValues.type
      if (this.filterValues.label) url += '&label=' + encodeURIComponent(this.filterValues.label)

      try {
        let res = await axios.get(url, config)
        let data = res.data
        this.totalRows = data.total_rows || 0
        this.customFields = (data.custom_fields || []).map(cf => {
          return {
            id: cf.id,
            type: _.find(this.customFieldTypes, {id: cf.type}),
            label: cf.label,
            required: cf.required,
            add_alert: cf.add_alert,
            interaction_required: cf.interaction_required,
            archived: cf.archived,
            dropdown_options: cf.dropdown_options || [],
            external_links: cf.external_links || [],
            policies: cf.policies || [],
            visibility: cf.visibility || 0,
            visibility_conditions: cf.visibility_conditions || [],
            gift_card_type_visibility_conditions: cf.gift_card_type_visibility_conditions || [],
            show_when_gift_card_in_cart: cf.show_when_gift_card_in_cart || 0,
            template_count: cf.template_count || 0,
            waiver_count: cf.waiver_count || 0,
            internal_booking_count: cf.internal_booking_count || 0,
          }
        })
        this.loading = false
      } catch (err) {
        this.loading = false
        console.error(err)
        if (err.response && err.response.status === 401) {
          swal({
            title: 'Error',
            html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
            type: 'error',
          }).then(() => {
            this.$router.push('/')
          })
        } else {
          swal('Error!', 'Something went wrong', 'error')
        }
      }
    },
    async saveField(item) {
      if (!item.label || !item.label.trim()) {
        swal('Error', 'Please enter a field label', 'error')
        return
      }

      const eventScope = item.event_scope || 'all'
      const giftCardScope = item.gift_card_scope || 'none'
      const eventConditions = eventScope === 'specific' ? (item.visibility_conditions || []) : []
      const giftCardConditions = giftCardScope === 'specific' ? (item.gift_card_type_visibility_conditions || []) : []
      // Events encoding (inverse of editCustomFieldModal):
      //   'all'      → visibility=0, conditions=[]
      //   'none'     → visibility=1, conditions=[]
      //   'specific' → visibility=1, conditions populated
      const visibility = eventScope === 'all' ? 0 : 1
      // Gift cards encoding:
      //   'none'     → show_when_gift_card_in_cart=0, conditions=[]
      //   'any'      → show_when_gift_card_in_cart=1, conditions=[]
      //   'specific' → show_when_gift_card_in_cart=1, conditions populated
      const showWhenAnyGiftCard = giftCardScope === 'none' ? 0 : 1

      if (eventScope === 'none' && giftCardScope === 'none') {
        swal('Error', 'Pick at least one event or gift card rule', 'error')
        return
      }
      if (eventScope === 'specific' && eventConditions.length === 0) {
        swal('Error', 'Pick at least one event, or switch to "All events" or "None"', 'error')
        return
      }
      if (giftCardScope === 'specific' && giftCardConditions.length === 0) {
        swal('Error', 'Pick at least one gift card type, or switch to "Any gift card" or "None"', 'error')
        return
      }

      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.group.id + '/custom-fields'

      let data = {
        type: item.type.id,
        label: item.label,
        external_links: item.external_links,
        policies: item.policies,
        dropdown_options: item.dropdown_options,
        required: item.type.id === 1 ? 0 : item.required,
        add_alert: item.add_alert,
        interaction_required: item.interaction_required,
        visibility: visibility,
        visibility_conditions: eventConditions,
        gift_card_type_visibility_conditions: giftCardConditions,
        show_when_gift_card_in_cart: showWhenAnyGiftCard,
      }

      try {
        if (item.id) {
          data.action = 'update'
          await axios.put(url + '/' + item.id, data, config)
        } else {
          await axios.post(url, data, config)
        }
        this.modals.custom_field = false
        this.loading = false
        await this.getCustomFields()
      } catch (err) {
        this.loading = false
        console.error(err)
        if (err.response && err.response.status === 401) {
          swal({
            title: 'Error',
            html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
            type: 'error',
          }).then(() => {
            this.$router.push('/')
          })
        } else {
          const data = err.response && err.response.data
          let message = 'Something went wrong'
          if (typeof data === 'string' && data.trim()) {
            message = data
          } else if (data && typeof data === 'object' && data.error) {
            message = data.error
          }
          swal('Error!', message, 'error')
        }
      }
    },
    archiveFieldPrompt(item) {
      let message = 'This custom field will be archived.'
      const totalUsage = (item.template_count || 0) + (item.waiver_count || 0) + (item.internal_booking_count || 0)
      if (totalUsage > 0) {
        const parts = []
        if (item.template_count > 0) parts.push(`${item.template_count} booking site template${item.template_count > 1 ? 's' : ''}`)
        if (item.waiver_count > 0) parts.push(`${item.waiver_count} waiver${item.waiver_count > 1 ? 's' : ''}`)
        if (item.internal_booking_count > 0) parts.push(`${item.internal_booking_count} internal booking${item.internal_booking_count > 1 ? 's' : ''}`)
        message = `This field is used on ${parts.join(', ')}. Archiving it will remove it from all of them.`
      }
      swal({
        title: 'Are you sure?',
        text: message,
        type: 'warning',
        showCancelButton: true,
      }).then((response) => {
        if (response.value === true) {
          this.archiveField(item)
        }
      })
    },
    async archiveField(item) {
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.group.id + '/custom-fields/' + item.id

      try {
        await axios.delete(url, config)
        this.loading = false
        swal('Archived', 'Custom field has been archived', 'success')
        await this.getCustomFields()
      } catch (err) {
        this.loading = false
        console.error(err)
        swal('Error!', 'Something went wrong', 'error')
      }
    },
    async restoreField(item) {
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.group.id + '/custom-fields/' + item.id

      try {
        await axios.put(url, {action: 'restore'}, config)
        this.loading = false
        swal('Restored', 'Custom field has been restored', 'success')
        await this.getCustomFields()
      } catch (err) {
        this.loading = false
        console.error(err)
        swal('Error!', 'Something went wrong', 'error')
      }
    },
  },
  mounted() {
    this.getGames()
    this.getGiftCardTypes()
    // Default when the query omits count/page, otherwise Number(undefined) => NaN
    // builds a bad fetch URL.
    const q = this.$route.query || {}
    this.perPage = Number(q.count) || 10
    this.currentPage = Number(q.page) || 1
    this.loading = true
    // Seed FilterBar's flat values from the URL. FilterBar resolves saved-views
    // precedence and drives the first fetch via its initial `change`.
    this.filterValues = {
      type: q.type ? Number(q.type) : '',
      label: q.label || '',
      archived: q.archived === 'true',
    }
  }
}
</script>

<style scoped>
/* The tabs header owns the full-width hairline so it runs under the action
   button too; the rail's own border is suppressed (the active-tab underline
   still overlaps the wrapper border via its -1px margin). */
.tabs-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.tabs-header ::v-deep .tab-rail {
  border-bottom-color: transparent;
  min-width: 0;
}

.tabs-header__action {
  margin-top: 0;
  margin-bottom: 14px;
}

.white-content .tabs-header {
  border-bottom-color: rgba(0, 0, 0, 0.12);
}

.info-hint {
  display: block;
  margin-bottom: 12px;
  padding: 8px 12px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  background: rgba(225, 78, 202, 0.06);
  border-left: 2px solid rgba(225, 78, 202, 0.3);
  border-radius: 0 4px 4px 0;
  line-height: 1.5;
}
.dropdown-option-row {
  padding: 10px 0 6px 0;
}
.dropdown-option-row--bordered {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.popup-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
  letter-spacing: 0.03em;
  margin-bottom: 2px;
}
.cf-char-count {
  margin-top: -8px;
  margin-bottom: 8px;
  text-align: right;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
}

/* Empty state */
.cf-empty-state {
  text-align: center;
  padding: 2.5rem 1rem;
}
.cf-empty-state__hint {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  max-width: 400px;
  margin: 0.25rem auto 1rem;
}

/* Card list */
.cf-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.cf-card {
  background: rgba(128, 128, 128, 0.05);
  border-left: 3px solid #e14eca;
  border-radius: 0.375rem;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s ease;
}
.cf-card:hover {
  background: rgba(128, 128, 128, 0.12);
}
.cf-card--archived {
  opacity: 0.5;
  border-left-color: rgba(125, 125, 125, 0.4);
}
.cf-card__body {
  display: flex;
  align-items: center;
  gap: 14px;
}
.cf-card__type-icon {
  font-size: 1.1em;
  color: #e14eca;
  width: 28px;
  text-align: center;
  flex-shrink: 0;
}
.cf-card__info {
  flex: 1;
  min-width: 0;
}
.cf-card__label {
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cf-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 3px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
}
.cf-card__type-name {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  font-size: 10px;
}
.cf-card__meta-detail {
  display: inline-flex;
  align-items: center;
}
.cf-card__meta-detail--clickable {
  color: #ba54f5;
  cursor: pointer;
}
.cf-card__meta-detail--clickable:hover {
  text-decoration: underline;
}
.cf-card__meta-detail--dimmed {
  opacity: 0.6;
  font-style: italic;
}

/* Usage row — visually subordinate to the property meta row above it */
.cf-card__usage {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.55);
}
.cf-card__usage-label {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
}
.cf-card__usage-text {
  color: rgba(255, 255, 255, 0.75);
}
.cf-card__usage--unused {
  font-style: italic;
  opacity: 0.7;
}
.cf-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* Badges */
/* Shared field warning */
.cf-shared-warning {
  background: rgba(255, 178, 54, 0.1);
  border: 1px solid rgba(255, 178, 54, 0.3);
  border-radius: 0.375rem;
  padding: 10px 14px;
  font-size: 12px;
  color: #ffb236;
}
</style>

<style>
.white-content .empty-state-text {
  color: #344675;
}
.white-content .cf-empty-state__hint {
  color: rgba(0, 0, 0, 0.55);
}
.white-content .cf-card {
  background: rgba(0, 0, 0, 0.02);
}
.white-content .cf-card:hover {
  background: rgba(0, 0, 0, 0.05);
}
.white-content .cf-card__meta {
  color: rgba(0, 0, 0, 0.7);
}
.white-content .cf-card__usage {
  color: rgba(0, 0, 0, 0.5);
}
.white-content .cf-card__usage-label {
  color: rgba(0, 0, 0, 0.5);
}
.white-content .cf-card__usage-text {
  color: rgba(0, 0, 0, 0.7);
}
.white-content .dropdown-option-row--bordered {
  border-top-color: rgba(0, 0, 0, 0.1);
}
.white-content .popup-label {
  color: rgba(0, 0, 0, 0.55);
}
.white-content .cf-char-count {
  color: rgba(0, 0, 0, 0.45);
}
.white-content .cf-shared-warning {
  background: rgba(255, 178, 54, 0.08);
}
.white-content .info-hint {
  color: rgba(0, 0, 0, 0.55);
  background: rgba(225, 78, 202, 0.04);
  border-left-color: rgba(225, 78, 202, 0.2);
}
</style>
